import { Link } from "react-router-dom";
import { useCMSSettings } from "../../hooks/useCMSSettings";

export default function Footer() {
  const { settings, navItems } = useCMSSettings();
  
  // Parse nav items for footer location
  const footerNav = navItems.filter((n: any) => n.location === 'footer');
  // Temporary hardcoded grouping since DB only seeded 'Privacy Policy' & 'Terms'
  const servicesLinks = footerNav.filter((n: any) => n.href?.startsWith('/services'));
  const companyLinks = footerNav.filter((n: any) => ['/about', '/work', '/blog', '/careers', '/sitemap'].includes(n.href));
  const regionLinks = footerNav.filter((n: any) => ['/us', '/uk', '/uae', '/europe', '/india', '/locations'].includes(n.href));
  
  // If no dynamic sub-links exist, fallback so it doesn't look empty immediately
  const renderServices = servicesLinks.length > 0 ? servicesLinks : [
    { label: "Web Development", href: "/services/web-development" },
    { label: "Custom CRM", href: "/services/crm-development" },
    { label: "Custom ERP", href: "/services/erp-development" },
    { label: "AI Agents", href: "/services/ai-agents" },
  ];
  const renderRegions = regionLinks.length > 0 ? regionLinks : [
    { label: "United States", href: "/us" },
    { label: "United Kingdom", href: "/uk" },
    { label: "UAE & Dubai", href: "/uae" },
    { label: "India", href: "/india" },
  ];
  const renderCompany = companyLinks.length > 0 ? companyLinks : [
    { label: "About", href: "/about" },
    { label: "Case Studies", href: "/work" },
    { label: "Careers", href: "/careers" },
  ];

  const socialLinks = [
    { label: "LinkedIn", href: settings?.social_linkedin || "/contact" },
    { label: "Instagram", href: settings?.social_instagram || "/contact" },
    { label: "Twitter/X", href: settings?.social_twitter || "/contact" },
    { label: "YouTube", href: settings?.social_youtube || "/contact" },
    { label: "WhatsApp", href: settings?.social_whatsapp || "/contact" },
  ].filter(l => l.href && l.href !== '/contact'); // Show default or only non-empty ones

  // Additional legal links found in DB
  const legalLinks = footerNav.filter((n: any) => ['/privacy-policy', '/terms', '/terms-of-service'].includes(n.href));

  return (
    <>
      <footer
        style={{
          background: "#0A0A0A",
          borderTop: "0.5px solid rgba(212,168,67,0.15)",
          padding: "clamp(48px, 8vw, 80px) clamp(20px, 4vw, 40px) clamp(28px, 5vw, 40px)",
        }}
      >
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <div className="owl-footer-grid" style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr 1fr", gap: "clamp(32px, 5vw, 60px)", marginBottom: "clamp(40px, 6vw, 60px)" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                {settings?.logo_url ? (
                  <img src={settings.logo_url} alt={settings.site_name} style={{ height: 40, objectFit: "contain" }} />
                ) : (
                  <picture>
                    <source srcSet="/owl-logo.webp" type="image/webp" />
                    <img src="/owl-logo.png" alt="OWL" width={40} height={40} style={{ height: 40, width: 40, objectFit: "contain" }} />
                  </picture>
                )}
                <span
                  style={{
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontWeight: 700,
                    fontSize: 12,
                    letterSpacing: "0.3em",
                    color: "#D4A843",
                    textTransform: "uppercase",
                  }}
                >
                  {settings?.site_name || "Obsidian Web Labs"}
                </span>
              </div>
              <p
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 300,
                  fontSize: 13,
                  color: "#908A83",
                  lineHeight: 1.6,
                  marginBottom: 24,
                  maxWidth: 280,
                }}
              >
                {settings?.tagline || "Headquartered in New Delhi, India. Serving clients worldwide with enterprise software, AI, and digital solutions."}
              </p>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                {socialLinks.length > 0 ? socialLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      fontFamily: "'Barlow Condensed', sans-serif",
                      fontWeight: 700,
                      fontSize: 10,
                      letterSpacing: "0.15em",
                      color: "#908A83",
                      textTransform: "uppercase",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = "#D4A843";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = "#908A83";
                    }}
                  >
                    {link.label}
                  </a>
                )) : null}
              </div>
            </div>

            <div>
              <div
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 700,
                  fontSize: 10,
                  letterSpacing: "0.3em",
                  color: "#D4A843",
                  textTransform: "uppercase",
                  marginBottom: 20,
                }}
              >
                Services
              </div>
              {renderServices.map((service: any) => (
                <Link
                  key={service.label}
                  to={service.href}
                  style={{
                    display: "block",
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 300,
                    fontSize: 13,
                    color: "#908A83",
                    padding: "5px 0",
                    lineHeight: 1.5,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "#F8F4EC";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "#908A83";
                  }}
                >
                  {service.label}
                </Link>
              ))}
            </div>

            <div>
              <div
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 700,
                  fontSize: 10,
                  letterSpacing: "0.3em",
                  color: "#D4A843",
                  textTransform: "uppercase",
                  marginBottom: 20,
                }}
              >
                Company
              </div>
              {renderCompany.map((item: any) => (
                <Link
                  key={item.label}
                  to={item.href}
                  style={{
                    display: "block",
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 300,
                    fontSize: 13,
                    color: "#908A83",
                    padding: "5px 0",
                    lineHeight: 1.5,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "#F8F4EC";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "#908A83";
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </div>

            <div>
              <div
                style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontWeight: 700,
                  fontSize: 10,
                  letterSpacing: "0.3em",
                  color: "#D4A843",
                  textTransform: "uppercase",
                  marginBottom: 20,
                }}
              >
                Regions We Serve
              </div>
              {renderRegions.map((item: any) => (
                <Link
                  key={item.label}
                  to={item.href}
                  style={{
                    display: "block",
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 300,
                    fontSize: 13,
                    color: "#908A83",
                    padding: "5px 0",
                    lineHeight: 1.5,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "#F8F4EC";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "#908A83";
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          <div
            style={{
              borderTop: "0.5px solid rgba(212,168,67,0.12)",
              paddingTop: 24,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            <span
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 300,
                fontSize: 12,
                color: "#908A83",
              }}
            >
              © {new Date().getFullYear()} {settings?.site_name || "Obsidian Web Labs"}. All rights reserved.
            </span>
            <span
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 300,
                fontSize: 11,
                color: "#908A83",
              }}
            >
              Made in {settings?.hq_location || "Delhi, India"} | Serving businesses globally
            </span>
            <div style={{ display: "flex", gap: 20 }}>
              {(legalLinks.length > 0 ? legalLinks : [
                { label: "Privacy Policy", href: "/privacy-policy" },
                { label: "Terms of Service", href: "/terms-of-service" },
              ]).map((item: any) => (
                <Link
                  key={item.label}
                  to={item.href}
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 300,
                    fontSize: 12,
                    color: "#908A83",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "#F8F4EC";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "#908A83";
                  }}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </footer>

      <style>{`
        @media (max-width: 960px) {
          .owl-footer-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
          }
        }

        @media (max-width: 640px) {
          .owl-footer-grid {
            grid-template-columns: 1fr !important;
            gap: 36px !important;
          }
        }
      `}</style>
    </>
  );
}
